import { useCallback } from 'react';
import type { FormikHelpers } from 'formik';
import { Archive, FlaskConical, HeartCrack, Save, Settings } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import type { ApiError } from '@monetr/interface/api/client';
import { Button } from '@monetr/interface/components/Button';
import Card from '@monetr/interface/components/Card';
import FormTextField from '@monetr/interface/components/FormTextField';
import MForm from '@monetr/interface/components/MForm';
import MTopNavigation from '@monetr/interface/components/MTopNavigation';
import SelectCurrency from '@monetr/interface/components/SelectCurrency';
import Typography from '@monetr/interface/components/Typography';
import { useArchiveBankAccount } from '@monetr/interface/hooks/useArchiveBankAccount';
import { useCurrentLink } from '@monetr/interface/hooks/useCurrentLink';
import { useSelectedBankAccount } from '@monetr/interface/hooks/useSelectedBankAccount';
import { useUpdateBankAccount } from '@monetr/interface/hooks/useUpdateBankAccount';
import type { APIError } from '@monetr/interface/util/request';

interface BankAccountValues {
  name: string;
  currency: string;
}

export default function BankAccountSettingsPage(): JSX.Element {
  const { data: link } = useCurrentLink();
  const { data: bankAccount, isLoading, isError } = useSelectedBankAccount();
  const updateBankAccount = useUpdateBankAccount();
  const archiveBankAccount = useArchiveBankAccount();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const archive = useCallback(async () => {
    if (!bankAccount) {
      return Promise.resolve();
    }

    if (window.confirm(`Are you sure you want to archive bank account: ${bankAccount.name}`)) {
      return archiveBankAccount(bankAccount.bankAccountId).then(() => navigate('/'));
    }

    return Promise.resolve();
  }, [bankAccount, archiveBankAccount, navigate]);

  if (isLoading) {
    return (
      <div className='w-full h-full flex items-center justify-center flex-col gap-2'>
        <Typography size='5xl'>One moment...</Typography>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='w-full h-full flex items-center justify-center flex-col gap-2'>
        <HeartCrack className='dark:text-dark-monetr-content h-24 w-24' />
        <Typography size='5xl'>Something isn't right...</Typography>
        <Typography size='2xl'>We weren't able to load details for the bank account specified...</Typography>
      </div>
    );
  }

  async function submit(values: BankAccountValues, helpers: FormikHelpers<BankAccountValues>) {
    helpers.setSubmitting(true);

    return await updateBankAccount({
      bankAccountId: bankAccount.bankAccountId,
      name: values.name,
      currency: values.currency,
    })
      .then(() =>
        enqueueSnackbar('Updated bank account successfully', {
          variant: 'success',
          disableWindowBlurListener: true,
        }),
      )
      .catch((error: ApiError<APIError>) =>
        enqueueSnackbar(error?.response?.data?.error || 'Failed to update bank account', {
          variant: 'error',
          disableWindowBlurListener: true,
        }),
      )
      .finally(() => helpers.setSubmitting(false));
  }

  const initialValues: BankAccountValues = {
    name: bankAccount.name,
    currency: bankAccount.currency,
  };

  return (
    <MForm className='w-full h-full flex flex-col' initialValues={initialValues} onSubmit={submit}>
      <MTopNavigation
        base={`/bank/${bankAccount.bankAccountId}/transactions`}
        breadcrumb='Settings'
        icon={Settings}
        title={bankAccount.name}
      >
        {!bankAccount.deletedAt && Boolean(link?.getIsManual()) && (
          <Button onClick={archive} variant='destructive'>
            <Archive />
            Archive
          </Button>
        )}
        <Button type='submit' variant='primary'>
          <Save />
          Save Changes
        </Button>
      </MTopNavigation>
      <div className='w-full h-full overflow-y-auto min-w-0 p-4'>
        <div className='flex flex-col md:flex-row w-full gap-8 items-center md:items-stretch'>
          <div className='w-full md:w-1/2 flex flex-col items-center'>
            <Card className='w-full mb-4'>
              <Typography size='inherit'>
                <FlaskConical className='w-16 h-16' />
                This page is still a work in progress, however it has been made available to make it possible to switch
                the currencies for your bank account sooner. This page will be changed over the next several releases to
                improve the UX and functionality.
              </Typography>
            </Card>
            <FormTextField
              className='w-full'
              data-1p-ignore
              label='Name'
              name='name'
              placeholder='Bank account name...'
            />
            <SelectCurrency className='w-full' disabled={link?.getIsPlaid()} name='currency' />
          </div>
        </div>
      </div>
    </MForm>
  );
}
